import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Sidebar from 'components/ui/Sidebar';
import GlobalHeader from 'components/ui/GlobalHeader';
import { useRole } from 'context/RoleContext';
import { NOTIFICATION_COUNTS } from 'data/mockData';
import AIChatAssistant from 'components/ui/AIChatAssistant';
import { isExternalRole } from 'config/roleConfig';
import { NAV_SECTIONS, BOTTOM_SECTIONS, ROUTES } from 'config/navConfig';

// Flat list of all nav items that have a route path — built once at module scope
const ALL_NAV_ITEMS = [
  ...NAV_SECTIONS.flatMap(s => s.children ?? [s]),
  ...BOTTOM_SECTIONS.flatMap(s => s.children ?? [s]),
];

const pageVariants = {
  initial: {
    opacity: 0,
    y: 12,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.22,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.15,
      ease: [0.55, 0, 1, 0.45],
    },
  },
};

export default function AppLayout({ children }) {
  const { role, setRole } = useRole();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => localStorage.getItem('sd_sidebar_collapsed') === 'true'
  );
  const [notificationCounts] = useState(NOTIFICATION_COUNTS);
  const location = useLocation();
  const navigate = useNavigate();
  const prevRoleRef = useRef(role);

  // Role-change accessibility guard — stay on current page if new role can access it,
  // redirect to default landing page otherwise
  useEffect(() => {
    if (prevRoleRef.current === role) return;
    prevRoleRef.current = role;
    const navItem = ALL_NAV_ITEMS.find(item => item.path === location.pathname);
    if (navItem && !navItem.permission?.includes(role)) {
      navigate(isExternalRole(role) ? ROUTES.opportunities : ROUTES.dashboard);
    }
  }, [role]);

  // Pathname accessibility guard — catches initial mount and direct URL navigation
  useEffect(() => {
    const navItem = ALL_NAV_ITEMS.find(item => item.path === location.pathname);
    if (navItem && !navItem.permission?.includes(role)) {
      navigate(isExternalRole(role) ? ROUTES.opportunities : ROUTES.dashboard);
    }
  }, [location.pathname]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location?.pathname]);

  // Persist sidebar collapsed preference
  useEffect(() => {
    localStorage.setItem('sd_sidebar_collapsed', isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  // Close mobile sidebar on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sidebarWidth = isSidebarCollapsed ? 72 : 280;

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        isOpen={isSidebarOpen}
        onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
        onClose={() => setIsSidebarOpen(false)}
        userRole={role}
        notificationCounts={notificationCounts}
      />
      {/* Global Header */}
      <GlobalHeader
        sidebarWidth={window.innerWidth >= 1024 ? sidebarWidth : 0}
        isSidebarCollapsed={isSidebarCollapsed}
        currentRole={role}
        onRoleChange={setRole}
        onMobileMenuOpen={() => setIsSidebarOpen(true)}
      />
      {/* Main content area */}
      <main
        className="min-h-screen transition-all duration-250"
        style={{
          marginLeft: window.innerWidth >= 1024 ? `${sidebarWidth}px` : '0',
          paddingTop: 'var(--header-height)',
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location?.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100%' }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
      {!isExternalRole(role) && <AIChatAssistant />}
    </div>
  );
}
